import z from 'zod';

import { ItemStatusEnum } from '@/modules/common';
import { FileObject, OptionType } from '@/modules/common/types';
import { Mask } from '@/modules/masks/types';
import { Model } from '@/modules/models/types';
import { OverlayItem } from '@/modules/overlays/types';

import {
  EventModelPosition,
  EventParticipantStatus,
  EventPrinterStatus,
} from '../constants';
import { stationsSchema } from '../validations';

export enum EventQueryKeys {
  Stations = 'eventStations',
}

export interface Event {
  id: string;
  name: string;
  startDate: string;
  aiModel: string;
  status: ItemStatusEnum;
}

export interface EventsResponse {
  items: Event[];
}

export interface EventUploadUrlData {
  eventId: string;
  signedUrl: string;
}

export interface EventUploadUrlsData {
  eventId: string;
  signedUrl: string;
  idleStateVideoPublicUrl: string;
}

export interface EventPrinter {
  name: string;
  type: string;
  printerId: string;
  status: EventPrinterStatus;
}

export interface EventModelSize {
  quantity: string;
  sizeId: string;
}

export interface EventStyle {
  id: string;
  name: string;
  imagePublicUrl: string;
}

export interface EventModelDetails {
  model: Model;
  sizes: {
    quantity: number;
    size: OptionType;
  }[];
  sides: EventModelPosition[];
}

export interface EventMask extends Mask {
  overlays: OverlayItem;
}

export interface EventDetails extends Event {
  publicUrl: string;
  idleStateVideoPublicUrl: string;
  gptEnabled: boolean;
  flagsAndInitialsEnabled: boolean;
  electricEffectEnabled: boolean;
  placementFeatureEnabled: boolean;
  printers?: EventPrinter[];
  desktopSecretKey: string;
  iosSecretKey: string;
  models: EventModelDetails[];
  masks: EventMask[];
  styles: EventStyle[];
  videoFileObject: FileObject | null;
  idleStateVideoFileObject: FileObject | null;
}

export interface CreateEventPayload {
  name: string;
  aiModel: string;
  startDate: string;
  gptEnabled: boolean;
  idleStateVideoPublicUrl: string;
  flagsAndInitialsEnabled: boolean;
  electricEffectEnabled: boolean;
  placementFeatureEnabled: boolean;
  printers?: EventPrinter[];
  models: {
    modelId: string;
    sizes: EventModelSize[];
    sides: EventModelPosition[];
  }[];
  styles: {
    styleId: string;
  }[];
  masks: {
    maskId: string;
    overlays: { overlayId: string }[];
  }[];
}

export interface UpdateEventPayload extends CreateEventPayload {
  id: string;
  status: ItemStatusEnum;
}

export interface EventParticipant {
  id: string;
  firstName: string;
  lastName: string;
  usagesLeft: number;
  email: string;
  company: string;
  jobTitle: string;
  phoneNumber: string;
  comment: string;
  type: string;
  externalId?: string;
  status: EventParticipantStatus;
  notificationNumbers: string[];
}

export interface EventParticipantsResponse {
  items: EventParticipant[];
}

export interface EventProductResponseItem {
  id: string;
  name: string;
  category: string;
  nickname: string;
  styleNumber: string;
  totalAmount: number;
  sizes: {
    id: string;
    size: string;
    amount: number;
  }[];
  createdAt: string;
}

export interface EventProduct extends Omit<EventProductResponseItem, 'sizes'> {
  size: {
    id: string;
    size: string;
    amount: number;
  };
}

export interface EventProductsResponse {
  items: EventProductResponseItem[];
}
export interface EventParticipantType {
  eventId: string;
  id: string;
  value: string;
  notificationNumbers: {
    id: string;
    phoneNumber: string;
    typeId: string;
  }[];
}

export interface CreateEventParticipantTypePayload {
  types: {
    value: string;
    phoneNumbers: string[];
  }[];
}

export interface EventStationResponseItem {
  id: string;
  name: string;
  accessTypes: {
    id: string;
    name: string;
  }[];
}

export interface MutateStationPayload {
  id: string;
  name: string;
  accessTypeIds: string[];
}

export type StationsFormValues = z.infer<typeof stationsSchema>;

export type ParticipantsTableFiltersType = {
  type: string;
  status: EventParticipantStatus;
};

export interface SendSmsPayload {
  message: string;
  participantIds: string[];
}
